import type { Auth0Client, LogoutOptions } from '@auth0/auth0-spa-js'
import React, { useContext, useMemo, useReducer, useEffect } from 'react'
import type { Auth0Claims } from 'paintscout'
import type { Auth0Session } from './util/session'
import {
  broadcastLogout,
  clearLogoutBroadcast,
  clearSkipAuthCache,
  getCachedAuth0User,
  shouldReloadForAuthStorageEvent,
  shouldSkipAuthCache
} from './util/login'
import { withTimeout } from './util/withTimeout'

/**
 * Upper bound on the silent token check in initializeAuth. Matches AUTH_TOKEN_TIMEOUT_MS in
 * `apps/shared/util/auth/createAuthTokenGetter.ts` (which cannot be imported here — dependency
 * direction is apps → packages): long enough for a refresh exchange behind the SDK's cross-tab
 * lock, short enough that a wedged SDK call degrades to the login redirect instead of an
 * indefinite spinner.
 */
const AUTH_INIT_TIMEOUT_MS = 15_000

export interface AuthProviderProps {
  auth0Client: Auth0Client
  scope: string
  audience: string
  appUrl?: string
  /**
   * Is called before the user is redirected to the login page.
   * Use this to clear any local data that might exist.
   */
  onLogout?: (companyId: string) => any

  children: React.ReactNode | ((props: AuthContextValue) => React.ReactNode)
}

export interface AuthContextValue {
  auth0Client?: Auth0Client
  session?: Auth0Session
  isAuthenticating: boolean
  /**
   * True once initializeAuth has settled (INITIALIZE dispatched). Optional so existing consumers
   * and test doubles that build a bare context don't break. Authorizer gates its login redirect
   * on this — before it, `session` may be null only because the async check hasn't finished.
   */
  initialized?: boolean

  authenticate: (opts?: { ignoreCache?: boolean }) => Promise<Auth0Session | null>
  logout: (options?: LogoutOptions) => any
  handleRedirectCallback: () => Promise<Auth0Session>
}

const AuthContext = React.createContext<AuthContextValue>({} as any)

// Grab the session stored in localstorage. shouldSkipAuthCache() and getCachedAuth0User()
// are both safeStorage-wrapped in util/login.ts, so a broken/unavailable localStorage
// (Safari ITP/private browsing/storage partitioning around the cross-origin Auth0
// redirect) resolves to "no cached session" here rather than crashing the render.
const getInitialSession = () => {
  if (shouldSkipAuthCache()) {
    return {}
  }

  return getCachedAuth0User() ?? {}
}

export default function AuthProvider({ auth0Client, onLogout, scope, audience, children, appUrl }: AuthProviderProps) {
  const cache = getInitialSession()
  const claims = cache?.decodedToken?.claims
  // Seed from cached claims even when their `exp` has passed. The ID token's `exp` says nothing
  // about whether the session is alive: the `PaintScout` client issues a 300-second ID token while
  // the access token that actually authorizes requests lives 86400 seconds, and since #22434 the
  // Apollo layer no longer refreshes every 5 minutes, so the cached ID token goes permanently
  // stale 5 minutes after login. Treating that as "no session" made every later page load bounce
  // through a full Auth0 redirect (ENG-1190). Stale-but-present claims are still the right
  // identity; whether tokens can actually be minted is decided async in initializeAuth below.
  const session = claims ? getSessionFromClaims(claims) : null

  const [state, dispatch] = useReducer(reducer, {
    isAuthenticating: true,
    initialized: false,
    session
  })

  useEffect(() => {
    const initializeAuth = async () => {
      let session: Auth0Session | null = null

      try {
        if (shouldSkipAuthCache()) {
          // After a logout or cross-app redirect, don't trust any cached session.
          // Force re-authentication. Any stale forceLogin localStorage keys are
          // harmless — consumeForceLogin() now only reads the cookie, which will
          // be present (and fresh) for a real post-logout flow.
          session = null
          clearSkipAuthCache()
        } else {
          const claims = (await auth0Client.getIdTokenClaims()) as unknown as Auth0Claims
          const expired = !!claims?.exp && claims.exp * 1000 < Date.now()
          if (!expired) {
            // Fresh claims (or none at all — the unchanged fresh-visitor path).
            session = getSessionFromClaims(claims)
          } else {
            // The cached ID token has outlived its 300s `exp`, but the access token (86400s) and
            // refresh token (30d) usually haven't. A bounded silent check decides whether the
            // session is actually alive, instead of forcing a full Auth0 redirect round trip on
            // every page load >5 minutes after login (ENG-1190). `cacheMode: 'on'`: a still-valid
            // cached access token settles this locally; only a genuinely expired one costs a
            // refresh exchange.
            const token = await withTimeout(auth0Client.getTokenSilently({ cacheMode: 'on' }), AUTH_INIT_TIMEOUT_MS)
            // The `typeof token === 'string'` check is load-bearing: `withTimeout` resolves
            // `undefined` when the timer wins, and some SDK paths can also resolve `undefined`
            // without a token. Only an actual token string proves the session is alive — anything
            // else must fall through to `session = null` so Authorizer sends the user to a real
            // login rather than reviving a session that cannot make authenticated requests.
            // A rejection here (`invalid_grant` — the grant is dead) is caught by the outer
            // catch below, which also lands on `session = null`; INITIALIZE always dispatches.
            if (typeof token === 'string') {
              // A real refresh rotates the ID token, so re-read; a cached-access-token hit
              // doesn't, so fall back to the stale-but-genuine claims.
              const refreshed = (await auth0Client.getIdTokenClaims()) as unknown as Auth0Claims
              session = getSessionFromClaims(refreshed ?? claims)
            } else {
              session = null
            }
          }
          if (session) {
            clearSkipAuthCache()
          }
        }
      } catch (err) {
        // A failure here (e.g. localStorage unavailable, or the Auth0 SDK's own cache
        // read throwing for the same reason) must not leave isAuthenticating stuck at
        // its initial `true` forever — that's a silent infinite loading state with no
        // error and no redirect. Fall through as "no session" so the app proceeds to
        // the normal not-authenticated → login flow instead.
        // eslint-disable-next-line no-console
        console.warn('[react-auth0] failed to initialize auth session', err)
        session = null
      }

      dispatch({
        type: 'INITIALIZE',
        payload: {
          isAuthenticating: !session,
          session
        }
      })
    }

    initializeAuth()
  }, [])

  // logout (or switch account) when another tab does — see shouldReloadForAuthStorageEvent for
  // why this doesn't just watch every @@auth0 cache-key mutation any more (ENG-1648).
  useEffect(() => {
    function listener(event: StorageEvent) {
      if (event.storageArea != localStorage) return

      if (shouldReloadForAuthStorageEvent(event.key, event.oldValue, event.newValue)) {
        clearLogoutBroadcast()
        window.onbeforeunload = () => null // prevent unsaved changes alert from stopping reload, they cant save anyway
        window.location.reload()
      }
    }

    window.addEventListener('storage', listener)

    return () => {
      window.removeEventListener('storage', listener)
    }
  }, [])

  const value = useMemo<AuthContextValue>(
    () => ({
      ...state,
      auth0Client,
      authenticate: async ({ ignoreCache } = {}) => {
        dispatch({ type: 'AUTHENTICATING' })

        try {
          const cacheModeOff = ignoreCache || !state.session
          await auth0Client.getTokenSilently({ cacheMode: cacheModeOff ? 'off' : 'on' })
          const claims = (await auth0Client.getIdTokenClaims()) as unknown as Auth0Claims
          const session = getSessionFromClaims(claims)
          if (session) {
            clearSkipAuthCache()
          }
          dispatch({ type: 'AUTHENTICATED', payload: session })

          return session
        } catch (e) {
          // A failed refresh must still clear `isAuthenticating`. Consumers gate their whole
          // tree on it — `apps/admin` renders <AppLoading /> and `apps/painter-view` renders
          // nothing while it is true — so leaving it set turns one transient token failure into
          // a permanently blank screen. The existing session is deliberately kept: a refresh
          // that couldn't reach Auth0 is no reason to discard claims that are still valid.
          dispatch({ type: 'AUTHENTICATION_FAILED' })

          if (e instanceof ProgressEvent) {
            return null
          }

          throw e
        }
      },
      logout: (opts) => {
        // Broadcast only after the call itself didn't throw — otherwise a mis-configured
        // `returnTo`/logoutParams could send other tabs into a reload for a logout that never
        // actually happened.
        auth0Client.logout({ ...opts, logoutParams: { federated: true, returnTo: appUrl, ...opts?.logoutParams } })
        broadcastLogout()
        onLogout?.(session?.app_metadata?.companyId)
        dispatch({ type: 'LOGOUT' })
      },
      handleRedirectCallback: async () => {
        await auth0Client.handleRedirectCallback()
        const claims = (await auth0Client.getIdTokenClaims()) as unknown as Auth0Claims
        const session = getSessionFromClaims(claims)
        if (session) {
          clearSkipAuthCache()
        }
        dispatch({ type: 'AUTHENTICATED', payload: session })

        return session
      }
    }),
    [auth0Client, state, audience, scope, onLogout, appUrl]
  )

  return (
    <AuthContext.Provider value={value}>
      {typeof children === 'function' ? children(value) : children}
    </AuthContext.Provider>
  )
}

interface AuthProviderState {
  isAuthenticating: boolean
  initialized: boolean
  session?: Auth0Session
}

function reducer(state: AuthProviderState, action: { type: string; payload?: any }): AuthProviderState {
  const { type, payload } = action

  switch (type) {
    case 'AUTHENTICATING':
      return {
        ...state,
        isAuthenticating: true
      }
    case 'AUTHENTICATED':
      return {
        ...state,
        isAuthenticating: false,
        session: payload
      }
    case 'LOGOUT':
      return {
        ...state,
        isAuthenticating: false
        // keep session so we don't get unexpected UI change before <Authorizer> processes the redirect
      }
    case 'AUTHENTICATION_FAILED':
      return {
        ...state,
        isAuthenticating: false
        // session left as-is — see the catch in `authenticate`
      }
    case 'UPDATE_SESSION':
      return {
        ...state,
        session: payload
      }
    case 'INITIALIZE':
      return {
        ...state,
        initialized: true,
        isAuthenticating: payload.isAuthenticating,
        session: payload.session
      }
  }

  return state
}

function getSessionFromClaims(claims: Auth0Claims): Auth0Session {
  if (!claims) {
    return null
  }

  let scopeDomain
  if (process.env.AUTH0_DEFAULT_AUDIENCE) {
    scopeDomain = `https://${process.env.AUTH0_DEFAULT_AUDIENCE}`
  } else {
    scopeDomain = 'https://paintscout.com'
  }

  return {
    user_id: claims.sub,
    email: claims.email,
    name: claims.name,
    picture: claims.picture,
    // @ts-ignore
    user_metadata: claims[`${scopeDomain}/user_metadata`],
    // @ts-ignore
    app_metadata: claims[`${scopeDomain}/app_metadata`],
    issued: claims.iat,
    expires: claims.exp
  }
}

/** Context consumers */
export const useAuth = () => useContext(AuthContext)
